import { clearPortalSentryUser } from '@admin-panel/observability';
import { LOCAL_GATEWAY_AUTH_BYPASS_ENABLED, PORTAL_NAME } from '@admin-panel/config/env';

type HttpErrorLike = {
  response?: {
    status?: unknown;
  };
  status?: unknown;
  statusCode?: unknown;
};

let hasHandledUnauthorizedSession = false;

const toStatusCode = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return undefined;
};

export const getHttpStatusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const maybeError = error as HttpErrorLike;

  return (
    toStatusCode(maybeError.response?.status) ??
    toStatusCode(maybeError.statusCode) ??
    toStatusCode(maybeError.status)
  );
};

export const isUnauthorizedHttpError = (error: unknown) => getHttpStatusCode(error) === 401;

export const resetUnauthorizedSessionHandling = () => {
  hasHandledUnauthorizedSession = false;
};

/**
 * Facts about this device, not the signed-in person, so they survive logout: the device identity
 * the next login request needs, Hub's confirmed alert sound test (a shared phone stays set up),
 * Hub's Local/Stage alert-setup preview mode, Hub's installation id and last setup report
 * (adoption tracking counts devices, not logins), and which operators chose "Ring for all my
 * facilities" on this device.
 */
export const DEVICE_STORAGE_KEYS = [
  'fingerprint',
  'hub-alert-setup-done',
  'hub-alert-setup-mode',
  'hub-alerts-ring-all',
  'hub-install-id',
  'hub-setup-report',
] as const;

export const clearClientStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  clearPortalSentryUser();
  const kept = DEVICE_STORAGE_KEYS.map(key => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  kept.forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
    }
  });
  sessionStorage.clear();
};

// Foundation's STN approval deep link (sent in approval emails), rebuilt from validated parts.
const stnApprovalReturn = (returnTo?: string, search?: string) => {
  if (PORTAL_NAME !== 'admin' || returnTo !== '/stocktransfer' || !search) {
    return undefined;
  }
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (params.get('stockTransferTab') !== 'approval' || !id || !/^[1-9]\d*$/.test(id)) {
    return undefined;
  }
  return `/stocktransfer?stockTransferTab=approval&id=${id}${params.get('showTrial') === '1' ? '&showTrial=1' : ''}`;
};

export const redirectToLogin = (returnTo?: string, search?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname !== '/login') {
    // Only expired Hub package views and Foundation STN approval links keep a destination.
    // Explicit logout passes no path; arbitrary/external redirect targets keep their existing behavior.
    const destination =
      PORTAL_NAME === 'warehouse' && returnTo && /^\/packages\/show\/[1-9]\d*$/.test(returnTo)
        ? returnTo
        : stnApprovalReturn(returnTo, search);
    window.location.replace(`/login${destination ? `?to=${encodeURIComponent(destination)}` : ''}`);
  }
};

export const handleUnauthorizedSessionExpiry = () => {
  if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
    return;
  }

  if (hasHandledUnauthorizedSession) {
    return;
  }

  hasHandledUnauthorizedSession = true;
  const returnTo = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const search = typeof window !== 'undefined' ? window.location.search : undefined;
  clearClientStorage();
  redirectToLogin(returnTo, search);
};
